/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap, cx, jsx } from '@atlaskit/css';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	CapacityPlanningActionType,
	type CapacityPlanningPayload,
} from '@atlassian/conversation-assistant-message-actions-api/capacity-planning';

import { getContributorDataId, getWeeklyHours } from '../capacity-planning-utils';
import {
	type HydratedWork,
	type CapacityPlanningViewType,
	VIEW_TYPE,
	type HydratedUser,
} from '../types';
import WorkItemNode from '../work-item-node/WorkItemNode';

import type { ActionItemProps } from './types';

const checkboxLabelMessage = defineMessage({
	id: 'capacity-planning.action-item-checkbox',
	defaultMessage: 'Select action',
	description:
		'Accessible label for a checkbox in the capacity planning action list. The checkbox allows the user to select or deselect an individual action row for batch operations.',
});
const acceptLabelMessage = defineMessage({
	id: 'capacity-planning.action-item-accept',
	defaultMessage: 'Accept',
	description:
		'The text is shown on an icon button in the capacity planning action row. When clicked, the user confirms and applies the suggested action (such as allocating a contributor to work).',
});
const dismissLabelMessage = defineMessage({
	id: 'capacity-planning.action-item-dismiss',
	defaultMessage: 'Dismiss',
	description:
		'The text is shown on an icon button in the capacity planning action row. When clicked, the user dismisses and removes the suggested action without applying it.',
});
const addContributorMessage = defineMessage({
	id: 'capacity-planning.action-item.add-person-to-view',
	defaultMessage: 'Add to capacity plan',
	description:
		'The text is shown as a label in an action row within the capacity planning panel. It indicates that the action will add a contributor (team member) or a work item to the current capacity plan view.',
});
const allocateToWorkMessage = defineMessage({
	id: 'capacity-planning.action-item.allocate-to-work',
	defaultMessage: 'Allocate to {workItem}',
	description:
		'The text is shown as a label in an action row within the capacity planning panel. It indicates that the action will allocate a contributor to a specific work item. The placeholder {workItem} is substituted with a smart link to the work item (such as a project or goal).',
});
const allocateToContributorMessage = defineMessage({
	id: 'capacity-planning.action-item.allocate-to-contributor',
	defaultMessage: 'Allocate to "{contributorName}"',
	description:
		'The text is shown as a label in an action row within the capacity planning panel. It indicates that the action will allocate a work to a contributor. The placeholder "{contributorName}" is substituted with the name of the contributor.',
});
const createNonProjectWorkMessage = defineMessage({
	id: 'capacity-planning.action-item.create-non-project-work',
	defaultMessage: 'Allocate to {nonProjectWorkItem}',
	description:
		'The text is shown as a label in an action row within the capacity planning panel. It indicates that the action will create and allocate the contributor to a non-project work item. The placeholder {nonProjectWorkItem} is substituted with the name of the non-project work (for example: "Training" or "Meetings").',
});
const allocateContributionMessage = defineMessage({
	id: 'capacity-planning.action-item.allocate-from-to-at',
	defaultMessage: 'Allocate to {workItem} from {startDate} to {endDate} at {value}{valueUnit}',
	description:
		'The text is shown as a label in an action row within the capacity planning panel. It describes a contribution allocation with specific dates and capacity. The placeholder {workItem} is a smart link to the work item. The placeholder {startDate} is the allocation start date (for example: "Jan 1"). The placeholder {endDate} is the allocation end date (for example: "Mar 31"). The placeholder {value} is the numeric capacity value and {valueUnit} is the unit (%, hours, or days).',
});
const allocateContributionHoursMessage = defineMessage({
	id: 'capacity-planning.action-item.allocate-from-to-hours',
	defaultMessage:
		'Allocate to {workItem} for {value} hours each week from {startDate} to {endDate}',
	description: 'Label for allocating hours to a work item with specific dates.',
});
const allocateContributionHoursSingleDayMessage = defineMessage({
	id: 'capacity-planning.action-item.allocate-hours-single-day',
	defaultMessage: 'Allocate to {workItem} for {value} hours on {startDate}',
	description: 'Label for allocating hours to a work item for a single day.',
});
const allocateContributionDaysMessage = defineMessage({
	id: 'capacity-planning.action-item.allocate-from-to-days',
	defaultMessage: 'Allocate to {workItem} for {value} days from {startDate} to {endDate}',
	description: 'Label for allocating days to a work item with specific dates.',
});
const allocateContributionDaysSingleDayMessage = defineMessage({
	id: 'capacity-planning.action-item.allocate-days-single-day',
	defaultMessage: 'Allocate to {workItem} on {startDate}',
	description: 'Label for allocating to a work item for a single day.',
});
const allocateContributionPercentageMessage = defineMessage({
	id: 'capacity-planning.action-item.allocate-from-to-percentage',
	defaultMessage: 'Allocate to {workItem} from {startDate} to {endDate} at {value}{valueUnit}',
	description: 'Label for allocating percentage to a work item with specific dates.',
});
const allocateContributionPercentageSingleDayMessage = defineMessage({
	id: 'capacity-planning.action-item.allocate-percentage-single-day',
	defaultMessage: 'Allocate to {workItem} on {startDate} at {value}{valueUnit}',
	description: 'Label for allocating percentage to a work item for a single day.',
});
const removeFromWorkMessage = defineMessage({
	id: 'capacity-planning.action-item.remove-from-work',
	defaultMessage: 'Remove from {workItem}',
	description:
		'The text is shown as a label in an action row within the capacity planning panel. It indicates that the action will remove the contributor from a specific work item. The placeholder {workItem} is substituted with a smart link to the work item being removed.',
});
const unitPercentMessage = defineMessage({
	id: 'capacity-planning.action-item.unit-percent',
	defaultMessage: '%',
	description:
		"The text is shown as a unit suffix after a numeric value in capacity planning action labels. It indicates the allocation value is expressed as a percentage of the contributor's capacity.",
});
const unitHoursMessage = defineMessage({
	id: 'capacity-planning.action-item.unit-hours',
	defaultMessage: ' hours',
	description:
		'The text is shown as a unit suffix after a numeric value in capacity planning action labels. It indicates the allocation value is expressed in hours per week or period.',
});
const unitDaysMessage = defineMessage({
	id: 'capacity-planning.action-item.unit-days',
	defaultMessage: ' days',
	description:
		'The text is shown as a unit suffix after a numeric value in capacity planning action labels. It indicates the allocation value is expressed in days per week or period.',
});
const removeContributorMessage = defineMessage({
	id: 'capacity-planning.action-item.remove-person-from-view',
	defaultMessage: 'Remove from capacity plan',
	description:
		'The text is shown as a label in an action row within the capacity planning panel. It indicates that the action will remove a contributor (team member) from the current capacity plan view entirely.',
});
const renameNonProjectWorkMessage = defineMessage({
	id: 'capacity-planning.action-item.rename-non-project-work',
	defaultMessage: 'Rename {currentName} to {newName}',
	description: 'Label for renaming a non-project work item.',
});
const actionCompletedMessage = defineMessage({
	id: 'capacity-planning.action-completed',
	defaultMessage: 'Action completed',
	description:
		'Accessible label for a checkmark icon shown in the capacity planning action row. The icon appears after an action has been successfully applied, indicating completion to screen reader users.',
});
const actionFailedMessage = defineMessage({
	id: 'capacity-planning.action-failed',
	defaultMessage: 'Action failed',
	description:
		'Accessible label for a cross icon shown in the capacity planning action row. The icon appears when an action fails to be applied, indicating the error status to screen reader users.',
});

const ICON_COLUMN_WIDTH = 24;

const styles = cssMap({
	root: {
		display: 'grid',
		gridTemplateColumns: `${ICON_COLUMN_WIDTH}px 1fr auto`,
		alignItems: 'center',
		marginLeft: token('space.negative.050'),
		marginRight: token('space.negative.050'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	label: {
		gridColumn: 2,
		paddingLeft: token('space.075'),
		minWidth: '0px',
		maxWidth: '100%',
		/** `overflow: hidden` clipped inline smart card titles; children handle truncation (e.g. Card `truncateInline`). */
		overflow: 'visible',
	},
	labelContent: {
		minWidth: '0px',
		maxWidth: '100%',
		overflow: 'visible',
		whiteSpace: 'normal',
		wordBreak: 'break-word',
	},
	actionButtons: {
		opacity: 1,
		gridColumn: 3,
	},
	actionButtonsHidden: {
		opacity: 0,
		pointerEvents: 'none',
	},
	checkbox: {
		opacity: 1,
		display: 'flex',
		justifyContent: 'center',
	},
	checkboxHidden: {
		opacity: 0,
	},
	statusIcon: {
		gridColumn: 1,
		width: `${ICON_COLUMN_WIDTH}px`,
		height: `${ICON_COLUMN_WIDTH}px`,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		justifySelf: 'center',
	},
});

function ActionItemStatus({
	isChecked,
	onToggle,
	showCheckbox,
	status,
}: {
	isChecked: boolean;
	onToggle: (checked: boolean) => void;
	showCheckbox: boolean;
	status: string;
}): React.ReactNode {
	const { formatMessage } = useIntl();
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onToggle(!isChecked);
		e.target.blur();
	};

	switch (status) {
		case 'started':
			return <Spinner size="small" interactionName="load" />;
		case 'finished':
			return (
				<CheckMarkIcon
					label={formatMessage(actionCompletedMessage)}
					color={token('color.icon.success')}
					size="medium"
				/>
			);
		case 'errored':
		case 'cancelled':
			return (
				<CrossIcon
					label={formatMessage(actionFailedMessage)}
					color={token('color.icon.danger')}
					size="medium"
				/>
			);
		default:
			return (
				<Box xcss={cx(styles.checkbox, !showCheckbox && styles.checkboxHidden)}>
					<Checkbox
						aria-label={formatMessage(checkboxLabelMessage)}
						isChecked={isChecked}
						onChange={handleChange}
					/>
				</Box>
			);
	}
}

function ActionItemLabel({
	payload,
	workById,
	viewType,
	hydratedUsers,
}: {
	payload: CapacityPlanningPayload;
	workById: Map<string, HydratedWork>;
	viewType: CapacityPlanningViewType;
	hydratedUsers: Map<string, HydratedUser>;
}): React.ReactNode {
	const { formatDate, formatMessage } = useIntl();
	const formatContributionDate = (dateString: string): string => {
		const timestamp = Date.parse(dateString);
		if (Number.isNaN(timestamp)) {
			return dateString;
		}
		const date = new Date(timestamp);
		return formatDate(date, {
			month: 'short',
			day: 'numeric',
		});
	};

	const getValueUnit = (valueType: string): string => {
		switch (valueType?.toUpperCase()) {
			case 'PERCENTAGE':
				return formatMessage(unitPercentMessage);
			case 'HOURS':
				return formatMessage(unitHoursMessage);
			case 'DAYS':
				return formatMessage(unitDaysMessage);
			default:
				return '';
		}
	};

	switch (payload.action) {
		case CapacityPlanningActionType.ADD_CONTRIBUTOR_WORK: {
			if (viewType === VIEW_TYPE.PEOPLE) {
				// note: keep it as FormattedMessage otherwise the work item will be rendered as a string instead of a React node
				return (
					<FormattedMessage
						{...allocateToWorkMessage}
						values={{
							workItem: <WorkItemNode payload={payload} workById={workById} />,
						}}
					/>
				);
			}

			const contributorDataId = getContributorDataId(payload);
			return (
				<FormattedMessage
					{...allocateToContributorMessage}
					values={{
						contributorName:
							(contributorDataId ? hydratedUsers.get(contributorDataId)?.name : undefined) ??
							contributorDataId ??
							'',
					}}
				/>
			);
		}
		case CapacityPlanningActionType.ADD_CONTRIBUTORS:
		case CapacityPlanningActionType.ADD_WORKS:
			return formatMessage(addContributorMessage);
		case CapacityPlanningActionType.CREATE_NON_PROJECT_WORK:
			return formatMessage(createNonProjectWorkMessage, {
				nonProjectWorkItem: `'${payload.inputs.name}'`,
			});
		case CapacityPlanningActionType.REMOVE_CONTRIBUTOR_WORK: {
			const contributorDataId = getContributorDataId(payload);
			let workItem: React.ReactNode = '';

			if (viewType === VIEW_TYPE.PEOPLE) {
				workItem = <WorkItemNode payload={payload} workById={workById} />;
			} else if (contributorDataId) {
				workItem = `"${hydratedUsers.get(contributorDataId)?.name ?? ''}"`;
			}

			return (
				<FormattedMessage
					{...removeFromWorkMessage}
					values={{
						workItem,
					}}
				/>
			);
		}
		case CapacityPlanningActionType.REMOVE_CONTRIBUTORS:
		case CapacityPlanningActionType.REMOVE_WORKS:
			return formatMessage(removeContributorMessage);

		case CapacityPlanningActionType.UPDATE_CONTRIBUTIONS: {
			const first = payload.inputs.contributions[0]!;
			const valueType = first.valueType?.toUpperCase();
			const isSingleDay = first.startDate === first.endDate;

			// For multi-day HOURS allocations, convert the total hours to a weekly equivalent
			// so the card reads "X hours each week" rather than the raw total.
			const displayValue =
				valueType === 'HOURS' && !isSingleDay
					? getWeeklyHours(first.value, first.startDate, first.endDate)
					: first.value;

			let messageToUse;
			switch (valueType) {
				case 'HOURS':
					messageToUse = isSingleDay
						? allocateContributionHoursSingleDayMessage
						: allocateContributionHoursMessage;
					break;
				case 'DAYS':
					messageToUse = isSingleDay
						? allocateContributionDaysSingleDayMessage
						: allocateContributionDaysMessage;
					break;
				case 'PERCENTAGE':
					messageToUse = isSingleDay
						? allocateContributionPercentageSingleDayMessage
						: allocateContributionPercentageMessage;
					break;
				default:
					messageToUse = allocateContributionMessage;
					break;
			}

			return (
				<FormattedMessage
					{...messageToUse}
					values={{
						endDate: formatContributionDate(first.endDate),
						startDate: formatContributionDate(first.startDate),
						value: displayValue,
						valueUnit: getValueUnit(first.valueType),
						workItem:
							viewType === VIEW_TYPE.PEOPLE ? (
								<WorkItemNode payload={payload} workById={workById} />
							) : getContributorDataId(payload) ? (
								`"${hydratedUsers.get(getContributorDataId(payload)!)?.name ?? ''}"`
							) : (
								''
							),
					}}
				/>
			);
		}

		case CapacityPlanningActionType.RENAME_NON_PROJECT_WORK: {
			const currentName = workById.get(payload.inputs.id)?.name ?? payload.inputs.id;
			return (
				<FormattedMessage
					{...renameNonProjectWorkMessage}
					values={{ currentName: `'${currentName}'`, newName: `'${payload.inputs.name}'` }}
				/>
			);
		}

		default:
			return '';
	}
}

export function ActionItem({
	action,
	getStatus,
	isChecked,
	onCancel,
	onConfirm,
	onToggle,
	payload,
	workById,
	viewType,
	hydratedUsers,
}: ActionItemProps): JSX.Element {
	di(useAnalytics);
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [isHovered, setIsHovered] = useState(false);
	const onMouseEnter = useCallback(() => setIsHovered(true), []);
	const onMouseLeave = useCallback(() => setIsHovered(false), []);

	const status = getStatus(action);
	const isDisabled = status !== 'idle';
	const effectiveStatus = isDisabled ? status : (action.status ?? 'idle');
	const isSubmitting = effectiveStatus === 'started';
	const isActionCompleted =
		effectiveStatus === 'finished' ||
		effectiveStatus === 'errored' ||
		effectiveStatus === 'cancelled';
	const showCheckbox = !isActionCompleted && (isHovered || isChecked);
	const showActionButtons = !isActionCompleted && isHovered;

	const handleAccept = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoCapacityPlanningSingleAccept',
			eventType: 'ui',
			attributes: {
				actionType: payload.action,
				viewType,
			},
		});
		return onConfirm(action);
	}, [sendAnalyticsEvent, payload.action, viewType, action, onConfirm]);

	const handleDismiss = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoCapacityPlanningSingleDismiss',
			eventType: 'ui',
			attributes: {
				actionType: payload.action,
				viewType,
			},
		});
		return onCancel(action);
	}, [sendAnalyticsEvent, payload.action, viewType, action, onCancel]);

	return (
		<Box
			xcss={styles.root}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			testId={`capacity-planning-action-${action.invocationId}`}
		>
			<Box xcss={styles.statusIcon}>
				<ActionItemStatus
					isChecked={isChecked}
					onToggle={onToggle}
					showCheckbox={showCheckbox}
					status={effectiveStatus}
				/>
			</Box>
			<Inline alignBlock="center" xcss={styles.label}>
				<Box xcss={styles.labelContent}>
					<Text size="medium" color="color.text.subtle" as="span">
						<ActionItemLabel
							payload={payload}
							workById={workById}
							viewType={viewType}
							hydratedUsers={hydratedUsers}
						/>
					</Text>
				</Box>
			</Inline>
			<Inline
				xcss={cx(styles.actionButtons, !showActionButtons && styles.actionButtonsHidden)}
				space="space.050"
			>
				<IconButton
					icon={(iconProps) => <CheckMarkIcon {...iconProps} label="" />}
					label={formatMessage(acceptLabelMessage)}
					spacing="compact"
					appearance="subtle"
					onClick={handleAccept}
					isDisabled={isDisabled || isSubmitting}
				/>
				<IconButton
					icon={(iconProps) => <CrossIcon {...iconProps} label="" />}
					label={formatMessage(dismissLabelMessage)}
					spacing="compact"
					appearance="subtle"
					onClick={handleDismiss}
					isDisabled={isDisabled || isSubmitting}
				/>
			</Inline>
		</Box>
	);
}
